"use client";
import { Button, FormInput } from "@/components/common/form";
import ROUTES from "@/config/routes";
import { signIn } from "next-auth/react";
import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { credentialSchema } from "@/schema/loginSchema";
import { useRouter, useSearchParams } from "next/navigation";
import { showToast } from "@/utils/alert";
import { ProcessStatusType } from "@/@types/general";
import { ButtonLoader } from "@/components/common";
// import { encryptDataNetwork } from "@/utils/encryptor";
// import { login } from "@/libs/users";
import { handleApiError } from "@/utils/response";
import { useAuthStore } from "@/store/authStore";
import Image from "next/image";
import { cardBackground, logo } from "@/assets/images";

const LoginForm = () => {
  const [formStatus, setFormStatus] = useState<ProcessStatusType>("");
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl");
  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm({
    resolver: zodResolver(credentialSchema),
  });
  const [errorMessage, setErrorMessage] = useState("");
  const setEmail = useAuthStore((state) => state.setEmail);
  const setPassword = useAuthStore((state) => state.setPassword);
  const router = useRouter();

  const onSubmit = async (data: FieldValues) => {
    setErrorMessage("");
    setFormStatus("loading");
    try {
      // const response = await login(encryptDataNetwork(data));
      // if (response.data) {
      const res = await signIn("credentials", {
        redirect: false,
        email: data.email,
        password: data.password,
      });
      if (res?.error) {
        setEmail(data.email);
        setPassword(data.password);

        showToast({ title: res.error, icon: "error" });
        setErrorMessage(res.error);
        setFormStatus("error");
        return;
      } else {
        showToast({ title: "Welcome back!", icon: "success" });
        if (callbackUrl) {
          window.location.href = callbackUrl;
          return;
        }
        router.push(ROUTES.HOME()); // Redirect to dashboard after login
      }
      setFormStatus("success");
      // }
    } catch (error) {
      setFormStatus("error");
      await handleApiError(error, showToast); // now we pass the actual function
    }
  };

  const isDisabled = formStatus == "loading";

  return (
    <div className="flex flex-col gap-4 lg:bg-white w-full lg:shadow-xl lg:p-4 lg:rounded-xl lg:overflow-hidden lg:relative">
      <Image
        src={cardBackground}
        className="absolute top-0 right-0 z-0"
        alt="Logo"
      />
      <Image
        src={logo}
        className="absolute top-[120px] -translate-y-[120px] lg:top-[50px] lg:-translate-y-[50px] left-1/2 -translate-x-1/2 opacity-10 w-full z-[1]"
        alt="Logo "
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col max-lg:w-full max-lg:px-4 gap-2 max-w-full lg:w-[450px] relative z-[2]"
      >
        <div className="relative flex flex-col gap-4">
          <h2 className="font-bold lg:gap-4 text-primary text-4xl flex items-center mb-2">
            Login
          </h2>
          <div className="flex flex-col gap-4 mt-4">
            <AnimatePresence>
              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="alert alert-error font-bold text-sm text-left"
                >
                  <FaTimes /> {errorMessage}
                </motion.div>
              )}
            </AnimatePresence>
            <FormInput
              formFieldClassName="form-custom"
              register={register}
              error={errors.email?.message}
              name="email"
              label={"Email Address"}
              type="email"
              placeholder={"Enter your email address"}
              size="md"
              required
            />
            <FormInput
              register={register}
              formFieldClassName="form-custom"
              error={errors.password?.message}
              label={"Password"}
              name="password"
              type="password"
              placeholder={"Enter your password"}
              size="md"
              required
            />

            <Button
              disabled={isDisabled}
              shadow={true}
              size="md"
              type="submit"
              variant="primary"
            >
              {formStatus == "loading" ? <ButtonLoader /> : "Login"}
            </Button>
            {/* <div className="divider divider-accent text-xs">Or </div>

        <div className="flex gap-4 justify-center">
          <p>
            Forgot your password?{" "}
            <Link href={ROUTES.AUTH.SIGNUP()} className="font-bold text-accent">
              Click here
            </Link>
          </p>
        </div> */}
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
